import React, {useState} from 'react'
import StarterKit from "@tiptap/starter-kit";
import {useNavigate, useParams} from "react-router-dom";

import '../../styles/admin-page.css';
import '../../styles/blog-page.css';

import { SimpleEditor } from 'components/tiptap-templates/simple/simple-editor'
import {Button, IconButton, TextField} from "@mui/material";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArchive, faEdit, faBullhorn, faImage, faSave, faTrash, faUpload} from "@fortawesome/free-solid-svg-icons";
import ImageUploadDialog from "../../components/ImageUploadDialog";
import useAuth from "../../hooks/useAuth";
import {
    deleteBlog,
    getAllBlogs,
    getBlog,
    getBlogs, notifyBlogPost,
    postBlog,
    postBlogImage,
    postBlogMainImage,
    putBlog
} from "services/API/BlogpostAPI";
import useAsyncCallWithLoader from "../../hooks/useAPICallWithLoader";
import {convertFromRaw, convertToRaw} from "draft-js";
import {stateToHTML} from "draft-js-export-html";
import {generateJSON} from "@tiptap/html";
import {TextAlign} from "@tiptap/extension-text-align";
import {Image} from "@tiptap/extension-image";
import {Typography} from "@tiptap/extension-typography";
import {Selection} from "@tiptap/extensions";
import { ImageUploadNode } from "components/tiptap-node/image-upload-node/image-upload-node-extension"
import { HorizontalRule } from "components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension"
import {MAX_FILE_SIZE} from "../../lib/tiptap-utils";
import {BlogStatus, BlogStatusConfig} from "../../lib/blogStatus";
import ConfirmDialog from "../../components/ConfirmDialog";
import useShowNotification from "../../hooks/useShowNotification";
import {StatusBadge} from "../../components/StatusBadge";
const API_URL = process.env.REACT_APP_API_ENDPOINT;

const AdminEditBlogPage = () => {
    
    const { blogId } = useParams();
    const { auth } = useAuth();
    const [editor, setEditor] = useState(null);
    const { showSuccessNotification } = useShowNotification()

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmDialogBlog, setConfirmDialogBlog] = useState(null);
    
    const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
    const [notifyDialogBlog, setNotifyDialogBlog] = useState(null);

    const openDeleteConfirmDialog = (blog) => {
        setConfirmDialogBlog(blog);
        setConfirmDialogOpen(true);
    }
    
    const [blogpost, setBlogpost] = useState({
        date: "...",
        country: "...",
        title: "...",
        mainImagePath: null,
        mainImage: {
          id: null,
          path: null,
        },
        status: {
          name: 'Draft',
        },
        mainImageId: null,
        content: {
            type: "doc",
            content: [],
        }
    });
    
    const [editingDate, setEditingDate] = useState(false);
    const [editingCountry, setEditingCountry] = useState(false);
    const [editingTitle, setEditingTitle] = useState(false);
    
    const [mainImageDialogOpen, setMainImageDialogOpen] = useState(false);

    const confirmUploadMainImage = async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        const updatedBlogData = await postBlogMainImage(blogId, formData, auth.token);
        
        setBlogpost(oldBlogpost => ({
            ...oldBlogpost,
            mainImage: {
                path: updatedBlogData.mainImagePath,
                id: updatedBlogData.mainImageId,
            },
            mainImageId: updatedBlogData.mainImageId
        }));
    }
    
    const openNotifyDialog = (blog) => {
        setNotifyDialogOpen(true);
        setNotifyDialogBlog(blog);
    }

    const fetchBlogData = async (editor) => {
        const blogData = await getBlog(blogId);
        
        if (blogData.contentLegacy != null && blogData.content == null) {
            // Start migration
            blogData.content = migrateFromDraftToTipTap(blogData);
        }
        else {
            blogData.content = JSON.parse(blogData.content);
        }

        setBlogpost(blogData);
        editor.commands.setContent(blogData.content);
    }
    
    const onEditorCreate = (editor) => {
        setEditor(editor);
        onScreenOpenFetchData(editor);
    }
    
    const onScreenOpenFetchData = useAsyncCallWithLoader(fetchBlogData);
    
    const migrateFromDraftToTipTap = (blogData) => {
        const contentState = convertFromRaw(JSON.parse(blogData.contentLegacy));
        const html = stateToHTML(contentState);

        const jsonWithoutCarousel = generateJSON(html, [
            StarterKit.configure({
                horizontalRule: false,
                link: {
                    openOnClick: false,
                    enableClickSelection: true,
                },
            }),
            HorizontalRule,
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            Image,
            Typography,
            Selection,
            ImageUploadNode.configure({
                accept: "image/*",
                maxSize: MAX_FILE_SIZE,
                limit: 3,
                upload: handleImageUpload,
                onError: (error) => console.error("Upload failed:", error),
            }),
        ]);
        
        const contentArray = jsonWithoutCarousel.content.map(x => x.type === "image" ? ({type: "imageCarousel", attrs: { images: [ {src: x.attrs.src, alt: '' } ] }}) : x)
        const newJson = { ...jsonWithoutCarousel, content: contentArray };
        
        return newJson;
    }

    const saveBlog = async (statusId) => {
        if (!editor || !editor.view) return
        
        setBlogpost(oldBlogpost => ({
            ...oldBlogpost,
            statusId: statusId,
            content: editor.getJSON(),
        }))
        
        const blogpostObject = {
            ...blogpost,
            statusId: statusId,
            content: JSON.stringify(editor.getJSON()),
        }

        const blogResponse = await putBlog(blogId, auth.token, blogpostObject);
        
        if (blogResponse.status === 201) {
            showSuccessNotification(`Opgeslagen met status '${BlogStatusConfig[statusId].label}'`);
            fetchBlogData(editor);
        }
    }
    
    const handleImageUpload = async (file) => {
        // Validate file
        if (!file) {
            throw new Error("No file provided")
        }
        
        const formData = new FormData();
        formData.append('file', file);
        const postBlogImageResponse = await postBlogImage(blogId, formData, auth.token);

        return `Assets/${postBlogImageResponse.path}`;
    }

    return (
        <div>
            <div className='blog-page__background-image' style={ blogpost.mainImage ? { backgroundImage: `url(${API_URL}/Assets/${blogpost.mainImage.path})`} : null}>
                <div className='blog-page__background-black'/>
                <div className='blog-page__background-container'>
                    <div className='admin-page__status-badge-container'>
                        <StatusBadge status={blogpost.status}/>
                    </div>
                    <div className='admin-page__blog-status-buttons'>
                      {
                        blogpost.status.id === BlogStatus.DRAFT && (
                          <IconButton
                            onClick={() => saveBlog(BlogStatus.DRAFT)}
                            sx={{
                              margin: "5px",
                              width: "40px",
                              height: "40px",
                            }}>
                            <FontAwesomeIcon
                              icon={faSave}
                              style={{ color: "white" }}/>
                          </IconButton>
                        )
                      }
                      {
                        blogpost.status.id === BlogStatus.DRAFT && (
                          <IconButton
                            onClick={() => saveBlog(BlogStatus.PUBLISHED)}
                            sx={{
                              margin: "5px",
                              width: "40px",
                              height: "40px",
                            }}>
                            <FontAwesomeIcon
                              icon={faUpload}
                              style={{ color: "white" }}/>
                          </IconButton>
                        )
                      }
                        {
                          (blogpost.status.id === BlogStatus.PUBLISHED || blogpost.status.id === BlogStatus.ARCHIVED) && (
                            <IconButton
                              onClick={() => saveBlog(BlogStatus.DRAFT)}
                              sx={{
                                  margin: "5px",
                                  width: "40px",
                                  height: "40px",
                              }}>
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  style={{ color: "white" }}/>
                            </IconButton>
                          )
                        }
                      {
                        blogpost.status.id === BlogStatus.PUBLISHED && (
                          <IconButton
                            onClick={() => saveBlog(BlogStatus.ARCHIVED)}
                            sx={{
                              margin: "5px",
                              width: "40px",
                              height: "40px",
                            }}>
                            <FontAwesomeIcon
                              icon={faArchive}
                              style={{ color: "white" }}/>
                          </IconButton>
                        )
                      }
                      {
                        blogpost.status.id === BlogStatus.ARCHIVED && (
                          <IconButton
                            onClick={() => openDeleteConfirmDialog(blogpost)}
                            sx={{
                              margin: "5px",
                              width: "40px",
                              height: "40px",
                            }}>
                            <FontAwesomeIcon
                              icon={faTrash}
                              style={{ color: "white" }}/>
                          </IconButton>
                        )
                      }
                    </div>
                    <div className='blog-page__title-container'>
                        <div className='blog-page__date-separator-country-container'>
                            { editingDate ?
                            <TextField
                                value={blogpost.date}
                                onChange={(e) => {
                                    setBlogpost(oldBlogpost => ({
                                        ...oldBlogpost,
                                        date: e.target.value,
                                    }));
                                }}
                                onBlur={() => setEditingDate(false)}
                                variant="standard"
                                InputProps={{
                                    disableUnderline: true,
                                    style: { color: "white", textAlign: "center", width: "auto" }
                                }}
                                sx={{
                                    width: "fit-content",
                                    "& .MuiInputBase-input": {
                                        padding: 0,
                                        textAlign: "center",
                                        width: "10ch",
                                        color: "white",
                                        fontFamily: "Poppins",
                                        fontWeight: 300,
                                        textShadow: "1px 1px 1px #000",
                                        
                                    },
                                }}
                            />
                            : 
                            <span className='blog-page__date-separator-country text'
                                  {...(blogpost.status.id === BlogStatus.DRAFT && {
                                      onClick: () => setEditingDate(true),
                                      style: { cursor: "pointer" }
                                  })}
                            >
                                {blogpost.date}
                            </span> }
                            <span className='blog-page__date-separator-country text'>&nbsp;|&nbsp;</span>

                            { editingCountry ?
                                <TextField
                                    value={blogpost.country}
                                    onChange={(e) => {
                                        setBlogpost(oldBlogpost => ({
                                            ...oldBlogpost,
                                            country: e.target.value,
                                        }));
                                    }}
                                    onBlur={() => setEditingCountry(false)}
                                    variant="standard"
                                    InputProps={{
                                        disableUnderline: true,
                                        style: { color: "white", textAlign: "center" }
                                    }}
                                    sx={{
                                        width: "fit-content",
                                        "& .MuiInputBase-input": {
                                            padding: 0,
                                            fontFamily: "Poppins",
                                            fontWeight: 300,
                                            textShadow: "1px 1px 1px #000",
                                        },
                                    }}
                                />
                                :
                                <span className='blog-page__date-separator-country text'
                                  {...(blogpost.status.id === BlogStatus.DRAFT && {
                                      onClick: () => setEditingCountry(true),
                                      style: { cursor: "pointer" }
                                  })}
                                >
                                    {blogpost.country}
                                </span>
                            }
                        </div>
                        { editingTitle ?
                            <TextField
                                value={blogpost.title}
                                onChange={(e) => {
                                    setBlogpost(oldBlogpost => ({
                                        ...oldBlogpost,
                                        title: e.target.value,
                                    }));
                                }}
                                onBlur={() => setEditingTitle(false)}
                                variant="standard"
                                InputProps={{
                                    disableUnderline: true,
                                }}
                                sx={{
                                    width: "fit-content",

                                    "& .MuiInputBase-input": {
                                        color: "white",
                                        fontSize: "2rem",
                                        fontFamily: "Lora",
                                        fontWeight: "bold",
                                        textShadow: "1px 1px 1px #000",
                                        padding: 0,
                                        marginTop: "5px",
                                    },
                                }}
                            />
                            :
                            <h1 className='title blog-page__title'
                              {...(blogpost.status.id === BlogStatus.DRAFT && {
                                    onClick: () => setEditingTitle(true),
                                    style: { cursor: "pointer" }
                              })}
                            >
                                {blogpost.title}
                            </h1> 
                        }
                        
                    </div>
                    <div className='admin-page__blog-header-buttons'>
                        {
                          blogpost.status.id === BlogStatus.DRAFT && (
                            <IconButton
                              onClick={() => setMainImageDialogOpen(true)}
                              sx={{
                                  margin: "5px",
                                  width: "40px",
                                  height: "40px",
                              }}>
                                <FontAwesomeIcon
                                  icon={faImage}
                                  style={{ color: "white" }}/>
                            </IconButton>
                          )
                        }
                        {
                          blogpost.status.id === BlogStatus.PUBLISHED && (
                            <IconButton
                              onClick={() => openNotifyDialog(blogpost)}
                              sx={{
                                  margin: "5px",
                                  width: "40px",
                                  height: "40px",
                              }}>
                                <FontAwesomeIcon
                                  icon={faBullhorn}
                                  style={{ color: "white" }}/>
                            </IconButton>
                          )
                        }
                    </div>
                </div>
            </div>
            <div className='blog-page__blog-text-container'>
                <div className='blog-page__blog-text-text-container text'>
                    <SimpleEditor editable={blogpost.status.id === BlogStatus.DRAFT} handleImageUpload={handleImageUpload} onEditorCreate={onEditorCreate} />
                </div>
            </div>
            <ImageUploadDialog
                open={mainImageDialogOpen}
                setOpen={setMainImageDialogOpen}
                confirmCallback={confirmUploadMainImage}
            ></ImageUploadDialog>
            <DeleteConfirmDialog
              confirmDialogOpen={confirmDialogOpen}
              setConfirmDialogOpen={setConfirmDialogOpen}
              confirmDialogBlog={confirmDialogBlog}
              setConfirmDialogBlog={setConfirmDialogBlog}
            />
            <NotifyDialog 
               notifyDialogOpen={notifyDialogOpen}
               setNotifyDialogOpen={setNotifyDialogOpen}
               notifyDialogBlog={notifyDialogBlog}
               setNotifyDialogBlog={setNotifyDialogBlog}
            />
        </div>
    )
}

const NotifyDialog = ({
     notifyDialogOpen,
     setNotifyDialogOpen,
     notifyDialogBlog,
     setNotifyDialogBlog,
     }) => {
    const { auth } = useAuth();
    const { showSuccessNotification } = useShowNotification()
    
    const onCancel = () => {
        setNotifyDialogOpen(false);
    }
    const submit = async () => {
        const resp = await notifyBlogPost(notifyDialogBlog.id, auth.token);
        setNotifyDialogOpen(false);
        setNotifyDialogBlog(null);
        if (resp.status === 200) {
            showSuccessNotification("Mail is verstuurd!");
        }
    }

    const onSubmit = useAsyncCallWithLoader(submit);

    return (
      <ConfirmDialog
        title="Mail versturen?"
        body={`Weet je zeker dat je een mail wilt versturen naar alle subscribers over de blog "${notifyDialogBlog?.title}"?`}
        onCancel={onCancel}
        onSubmit={onSubmit}
        open={notifyDialogOpen}
        setOpen={setNotifyDialogOpen}
      />
    )
}

const DeleteConfirmDialog = ({
     confirmDialogOpen,
     setConfirmDialogOpen,
     confirmDialogBlog,
     setConfirmDialogBlog,
        }) => {
    const { auth } = useAuth();
    const navigate = useNavigate();
    const onCancel = () => {
        setConfirmDialogOpen(false);
        setConfirmDialogBlog(null);
    }
    const submit = async () => {
        await deleteBlog(confirmDialogBlog.id, auth.token);
        setConfirmDialogOpen(false);
        setConfirmDialogBlog(null);
        navigate('/admin/blogs')
    }

    const onSubmit = useAsyncCallWithLoader(submit);

    return (
      <ConfirmDialog
        title="Verwijderen?"
        body={`Weet je zeker dat je \"${confirmDialogBlog?.title}\" wilt verwijderen? No way back!`}
        onCancel={onCancel}
        onSubmit={onSubmit}
        open={confirmDialogOpen}
        setOpen={setConfirmDialogOpen}
      />
    )
}


export default AdminEditBlogPage
